import { useState } from 'react';
import { Search, Download, Send, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable, Column } from '@/components/common/DataTable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { useSuperContributions, useSubmitToSuperStream, useDownloadSuperContributionXML } from '@/hooks/useSuperannuation';
import { SuperContribution } from '@/types';
import { formatCurrency, formatDate } from '@/lib/utils';

const getStatusBadge = (status: string) => {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'> = {
    pending: 'secondary',
    submitted: 'info',
    accepted: 'success',
    rejected: 'destructive',
  };
  
  const icons = {
    pending: <Clock className="h-3 w-3 mr-1" />,
    submitted: <Send className="h-3 w-3 mr-1" />,
    accepted: <CheckCircle className="h-3 w-3 mr-1" />,
    rejected: <AlertCircle className="h-3 w-3 mr-1" />,
  };
  
  return (
    <Badge variant={variants[status] || 'default'} className="flex items-center w-fit capitalize">
      {icons[status as keyof typeof icons]}
      {status}
    </Badge>
  );
};

export function SuperContributionsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  const { toast } = useToast();
  const { data: contributionsData, isLoading } = useSuperContributions({
    status: statusFilter === 'all' ? undefined : statusFilter,
    page: currentPage,
    limit: pageSize,
  });
  
  const submitToSuperStream = useSubmitToSuperStream();
  const downloadXML = useDownloadSuperContributionXML();

  const contributions = contributionsData?.data || [];
  const totalItems = contributionsData?.meta?.total || 0;

  const handleSubmit = async (id: string) => {
    try {
      await submitToSuperStream.mutateAsync(id);
      toast({
        title: 'Success',
        description: 'Contribution batch submitted to SuperStream',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to submit to SuperStream',
        variant: 'destructive',
      });
    }
  };

  const handleDownload = async (id: string, batchId: string) => {
    try {
      const blob = await downloadXML.mutateAsync(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SuperStream-${batchId}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: 'Success',
        description: 'XML file downloaded successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to download XML',
        variant: 'destructive',
      });
    }
  };

  const columns: Column<SuperContribution>[] = [
    {
      key: 'batchId',
      header: 'Batch ID',
      sortable: true,
      cell: (row) => (
        <span className="font-medium">{row.batchId}</span>
      ),
    },
    {
      key: 'payRunNumber',
      header: 'Pay Run',
      sortable: true,
      cell: (row) => row.payRunNumber,
    },
    {
      key: 'periodEndDate',
      header: 'Period End',
      sortable: true,
      cell: (row) => formatDate(row.periodEndDate),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      cell: (row) => getStatusBadge(row.status),
    },
    {
      key: 'contributions',
      header: 'Contributions',
      sortable: true,
      cell: (row) => row.contributions.length,
    },
    {
      key: 'totalAmount',
      header: 'Total Amount',
      sortable: true,
      cell: (row) => <span className="font-bold">{formatCurrency(row.totalAmount)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      cell: (row) => (
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => handleDownload(row.id, row.batchId)}
            disabled={downloadXML.isPending}
          >
            <Download className="h-4 w-4" />
          </Button>
          {row.status === 'pending' && (
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => handleSubmit(row.id)}
              disabled={submitToSuperStream.isPending}
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Calculate totals by fund
  const fundTotals = contributions.reduce((acc, contribution) => {
    contribution.contributions.forEach((item) => {
      if (!acc[item.superFundName]) {
        acc[item.superFundName] = 0;
      }
      acc[item.superFundName] += item.amount;
    });
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Super Contributions"
        description="Manage and submit superannuation contributions via SuperStream"
      />

      {/* Totals by Fund */}
      <Card>
        <CardHeader>
          <CardTitle>Totals by Fund</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(fundTotals).map(([fundName, total]) => (
              <div key={fundName} className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground">{fundName}</p>
                <p className="text-xl font-bold">{formatCurrency(total)}</p>
              </div>
            ))}
            {Object.keys(fundTotals).length === 0 && (
              <p className="text-muted-foreground col-span-3 text-center py-4">
                No contribution data available
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search contributions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <DataTable
        data={contributions}
        columns={columns}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        emptyMessage="No super contributions found. Create contributions from a pay run to get started."
      />
    </div>
  );
}
